/**
 * Выдача временной ссылки на машину.
 *
 * Два шага, как у блокировки: сначала «поделиться», потом срок. Одним шагом
 * не выйдет — срок выбирает человек, а четыре кнопки, висящие в карточке
 * постоянно, отвлекают от главного и подталкивают нажать не думая.
 *
 * Ссылка отдаётся сразу в буфер обмена: её всё равно тут же вставят в
 * сообщение, и заставлять человека выделять текст мышью незачем.
 *
 * Выданные ссылки видны списком с кнопкой отзыва. Без этого списка ссылка,
 * отправленная не тому, оставалась бы жить до конца срока, и отменить её было
 * бы нечем.
 */

import { useCallback, useEffect, useState } from 'react'

import { ApiError, api, type ShareLink } from './api'
import { dateTime } from './format'
import { dict, type Lang } from './i18n'
import { shareUrl } from './shareUrl'

/**
 * На сколько выдаётся ссылка. Срок выбирает человек, а не приложение.
 *
 * Полчаса — «подойди к машине, она вон там»; сутки — «следи за доставкой
 * сегодня». Разница существенная: ссылка, отправленная не тому, живёт ровно
 * столько, сколько ей отмерили, и полчаса безопаснее суток настолько же,
 * насколько суток удобнее получаса. Выбирать за человека тут нельзя.
 */
const SPANS = [0.5, 1, 4, 24] as const

export function ShareButton({ deviceId, lang }: { deviceId: number; lang: Lang }) {
  const t = dict(lang)
  const [links, setLinks] = useState<ShareLink[]>([])
  const [asking, setAsking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      setLinks(await api.shares(deviceId))
    } catch {
      // Список ссылок — не то, ради чего стоит портить экран машины.
    }
  }, [deviceId])

  useEffect(() => {
    void load()
  }, [load])

  async function create(hours: number) {
    setBusy(true)
    setError('')
    try {
      const link = await api.share(deviceId, hours)
      const url = shareUrl(link.key, location.origin)
      try {
        await navigator.clipboard.writeText(url)
        setCopied(true)
      } catch {
        // Буфер бывает закрыт: браузер отказал в правах, соединение не
        // защищено, iOS не счёл нажатие жестом. Ссылка всё равно выдана и
        // видна в списке ниже полным текстом — оттуда её можно выделить
        // руками. Без этого выданная ссылка осталась бы недостижимой.
      }
      await load()
      setAsking(false)
    } catch (caught) {
      setError(caught instanceof ApiError && caught.message ? caught.message : t.networkError)
    } finally {
      setBusy(false)
    }
  }

  async function revoke(key: string) {
    setBusy(true)
    try {
      await api.unshare(key)
      await load()
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {asking ? (
        <>
          <div className="kv">
            <span>{t.shareHowLong}</span>
          </div>
          <div className="chips" role="group" aria-label={t.shareHowLong}>
            {SPANS.map((hours) => (
              <button key={hours} disabled={busy} onClick={() => void create(hours)}>
                {spanLabel(hours, t)}
              </button>
            ))}
          </div>
          <button className="act ghost" onClick={() => setAsking(false)} disabled={busy}>
            {t.confirmNo}
          </button>
        </>
      ) : (
        <button
          className="act share"
          onClick={() => {
            setCopied(false)
            setError('')
            setAsking(true)
          }}
        >
          {copied ? t.shareCopied : t.shareAction}
        </button>
      )}

      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      {links.map((link) => (
        <div className="link-row" key={link.key}>
          <div className="kv">
            <span>
              {t.shareUntil} {dateTime(link.expiresAt, lang)}
            </span>
            <button className="link-off" onClick={() => void revoke(link.key)} disabled={busy}>
              {t.shareRevoke}
            </button>
          </div>
          {/* Ссылка текстом, а не только в буфере. Нажатие выделяет её
              целиком: на телефоне выделить длинный адрес пальцем иначе
              почти невозможно. */}
          <input
            className="link-text"
            readOnly
            value={shareUrl(link.key, location.origin)}
            aria-label={t.shareAction}
            onFocus={(e) => e.currentTarget.select()}
            onClick={(e) => e.currentTarget.select()}
          />
        </div>
      ))}
    </>
  )
}

/** Подпись срока: до часа считаем минутами, дальше часами. */
function spanLabel(hours: number, t: ReturnType<typeof dict>): string {
  return hours < 1 ? `${Math.round(hours * 60)} ${t.unitMinute}` : `${hours} ${t.unitHour}`
}
